import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Loader2, Archive, RotateCcw, CalendarPlus, Pencil } from "lucide-react";
import api from "@/lib/api";
import { chf } from "./salairesUtils";
import EvenementDialog from "./EvenementDialog";

const F = ({ label, children, hint }) => (
  <div>
    <Label className="text-xs text-gray-600">{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <div className="text-[10px] text-gray-400 mt-0.5">{hint}</div>}
  </div>
);

const TYPE_LABEL = {
  accident: "Accident", maladie: "Maladie", conge_sans_solde: "Congé sans solde",
  avance: "Avance sur salaire", prime: "Prime", frais: "Remboursement de frais",
  indemnite: "Indemnité", changement_salaire: "Changement de salaire",
  changement_taux: "Changement de taux", regularisation: "Régularisation",
};

export default function SalairesEmployesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inclureArchives, setInclureArchives] = useState(false);
  const [edit, setEdit] = useState(null);
  const [evtFor, setEvtFor] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/salaires/employes", { params: { inclure_archives: inclureArchives } });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [inclureArchives]);

  useEffect(() => { fetch(); }, [fetch]);

  const archiver = async (e) => {
    try {
      await api.post(`/salaires/employes/${e.id}/archiver`, {});
      toast.success(`${e.prenom} ${e.nom} archivé`);
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const reactiver = async (e) => {
    try {
      await api.post(`/salaires/employes/${e.id}/reactiver`);
      toast.success(`${e.prenom} ${e.nom} réactivé`);
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="salaires-employes">
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => setEdit({ nouveau: true, taux_activite: 100, mode_remuneration: "mensuel" })}
                className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="employe-new-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau collaborateur
        </Button>
        <Button variant="outline" onClick={() => setInclureArchives((v) => !v)} data-testid="employe-toggle-archives">
          {inclureArchives ? "Masquer les archivés" : "Voir les archivés"}
        </Button>
      </div>

      <Card className="border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-400">Aucun collaborateur enregistré</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Collaborateur</th>
                  <th className="px-3 py-2">Fonction</th>
                  <th className="px-3 py-2">Taux</th>
                  <th className="px-3 py-2 text-right">Salaire brut</th>
                  <th className="px-3 py-2">LPP</th>
                  <th className="px-3 py-2">Imposition</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="employes-table">
                {items.map((e) => (
                  <tr key={e.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-gray-900">{e.prenom} {e.nom}</div>
                      <div className="text-[11px] text-gray-500">{e.no_avs || "N° AVS manquant"}</div>
                    </td>
                    <td className="px-3 py-2.5 text-gray-700">
                      {e.fonction || "—"}
                      {e.categorie_cct && <span className="ml-1 text-[10px] text-gray-400">CCT {e.categorie_cct}</span>}
                    </td>
                    <td className="px-3 py-2.5">{e.taux_activite || 100} %</td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {e.mode_remuneration === "horaire"
                        ? `${chf(e.salaire_horaire)} /h`
                        : chf(e.salaire_brut_mensuel)}
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      {e.lpp_assujetti ? (e.lpp_mode === "montant_caisse" ? "Montant caisse" : "Plan") : "Non assujetti"}
                    </td>
                    <td className="px-3 py-2.5 text-xs">
                      {e.imposition === "source" ? `Source ${e.bareme_is || ""} ${e.taux_is || 0} %` : "Ordinaire"}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {e.archive && <Badge variant="outline" className="mr-2 bg-gray-100 text-gray-500">Archivé</Badge>}
                      <Button size="sm" variant="ghost" onClick={() => setEvtFor(e)} title="Ajouter un événement salarial"
                              data-testid={`employe-evt-${e.id}`}>
                        <CalendarPlus className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEdit(e)} data-testid={`employe-edit-${e.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {e.archive ? (
                        <Button size="sm" variant="ghost" onClick={() => reactiver(e)} data-testid={`employe-reactiver-${e.id}`}>
                          <RotateCcw className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => archiver(e)} data-testid={`employe-archiver-${e.id}`}>
                          <Archive className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {edit && <EmployeDialog employe={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); fetch(); }} />}
      {evtFor && <EvenementDialog employe={evtFor} onClose={() => setEvtFor(null)} onSaved={() => setEvtFor(null)} />}
    </div>
  );
}

function EmployeDialog({ employe, onClose, onSaved }) {
  const nouveau = !!employe.nouveau;
  const [form, setForm] = useState({ ...employe });
  const [busy, setBusy] = useState(false);
  const [evts, setEvts] = useState([]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (nouveau) return;
    api.get(`/salaires/employes/${employe.id}`).then(({ data }) => {
      setForm((f) => ({ ...data, ...f }));
      setEvts(data.evenements || []);
    }).catch(() => {});
  }, [employe.id, nouveau]);

  const submit = async () => {
    if (!form.nom) { toast.error("Le nom est obligatoire"); return; }
    setBusy(true);
    try {
      const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      const payload = {
        ...form,
        taux_activite: num(form.taux_activite) ?? 100,
        salaire_brut_mensuel: num(form.salaire_brut_mensuel) ?? 0,
        salaire_horaire: num(form.salaire_horaire),
        heures_mensuelles_ref: num(form.heures_mensuelles_ref),
        treizieme_taux: num(form.treizieme_taux),
        treizieme_mois_versement: num(form.treizieme_mois_versement),
        taux_is: num(form.taux_is),
        taux_aanp_override: num(form.taux_aanp_override),
        taux_ijm_override: num(form.taux_ijm_override),
        lpp_salaire_assure_manuel: num(form.lpp_salaire_assure_manuel),
        lpp_cotisation_mensuelle_employe: num(form.lpp_cotisation_mensuelle_employe),
        lpp_cotisation_mensuelle_employeur: num(form.lpp_cotisation_mensuelle_employeur),
        cct_montant_mensuel: num(form.cct_montant_mensuel),
      };
      delete payload.nouveau; delete payload.evenements; delete payload.fiches;
      if (nouveau) await api.post("/salaires/employes", payload);
      else await api.patch(`/salaires/employes/${employe.id}`, payload);
      toast.success(nouveau ? "Collaborateur créé" : "Modifications enregistrées");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="employe-dialog">
        <DialogHeader>
          <DialogTitle>{nouveau ? "Nouveau collaborateur" : `${form.prenom || ""} ${form.nom || ""}`}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="identite">
          <TabsList className="w-full overflow-x-auto justify-start no-scrollbar flex-nowrap">
            <TabsTrigger value="identite" data-testid="emp-tab-identite">Identité</TabsTrigger>
            <TabsTrigger value="contrat" data-testid="emp-tab-contrat">Contrat</TabsTrigger>
            <TabsTrigger value="assurances" data-testid="emp-tab-assurances">Assurances</TabsTrigger>
            <TabsTrigger value="lpp" data-testid="emp-tab-lpp">LPP</TabsTrigger>
            <TabsTrigger value="impots" data-testid="emp-tab-impots">Impôts</TabsTrigger>
            {!nouveau && <TabsTrigger value="evenements" data-testid="emp-tab-evenements">Événements</TabsTrigger>}
          </TabsList>

          <TabsContent value="identite" className="mt-4 grid sm:grid-cols-2 gap-3">
            <F label="Prénom"><Input value={form.prenom || ""} onChange={(e) => set("prenom", e.target.value)} data-testid="emp-prenom" /></F>
            <F label="Nom"><Input value={form.nom || ""} onChange={(e) => set("nom", e.target.value)} data-testid="emp-nom" /></F>
            <F label="Date de naissance"><Input type="date" value={form.date_naissance || ""} onChange={(e) => set("date_naissance", e.target.value)} data-testid="emp-naissance" /></F>
            <F label="N° AVS"><Input value={form.no_avs || ""} onChange={(e) => set("no_avs", e.target.value)} placeholder="756.XXXX.XXXX.XX" data-testid="emp-avs" /></F>
            <F label="Adresse"><Input value={form.adresse || ""} onChange={(e) => set("adresse", e.target.value)} data-testid="emp-adresse" /></F>
            <div className="grid grid-cols-3 gap-2">
              <F label="NPA"><Input value={form.npa || ""} onChange={(e) => set("npa", e.target.value)} data-testid="emp-npa" /></F>
              <div className="col-span-2"><F label="Localité"><Input value={form.localite || ""} onChange={(e) => set("localite", e.target.value)} data-testid="emp-localite" /></F></div>
            </div>
            <F label="IBAN"><Input value={form.iban || ""} onChange={(e) => set("iban", e.target.value)} data-testid="emp-iban" /></F>
            <F label="Nationalité"><Input value={form.nationalite || ""} onChange={(e) => set("nationalite", e.target.value)} data-testid="emp-nationalite" /></F>
            <F label="Permis de séjour / travail"><Input value={form.permis || ""} onChange={(e) => set("permis", e.target.value)} placeholder="B, C, G, Suisse…" data-testid="emp-permis" /></F>
            <F label="Pays de résidence"><Input value={form.pays_residence || ""} onChange={(e) => set("pays_residence", e.target.value)} data-testid="emp-pays" /></F>
          </TabsContent>

          <TabsContent value="contrat" className="mt-4 grid sm:grid-cols-2 gap-3">
            <F label="Date d'engagement"><Input type="date" value={form.date_engagement || ""} onChange={(e) => set("date_engagement", e.target.value)} data-testid="emp-engagement" /></F>
            <F label="Date de sortie"><Input type="date" value={form.date_sortie || ""} onChange={(e) => set("date_sortie", e.target.value)} data-testid="emp-sortie" /></F>
            <F label="Fonction"><Input value={form.fonction || ""} onChange={(e) => set("fonction", e.target.value)} data-testid="emp-fonction" /></F>
            <F label="Qualification"><Input value={form.qualification || ""} onChange={(e) => set("qualification", e.target.value)} data-testid="emp-qualification" /></F>
            <F label="Statut">
              <Select value={form.statut || "employe"} onValueChange={(v) => set("statut", v)}>
                <SelectTrigger data-testid="emp-statut"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employe">Employé</SelectItem>
                  <SelectItem value="apprenti">Apprenti</SelectItem>
                  <SelectItem value="cadre">Cadre</SelectItem>
                  <SelectItem value="auxiliaire">Auxiliaire</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Catégorie CCT UPSA"><Input value={form.categorie_cct || ""} onChange={(e) => set("categorie_cct", e.target.value)} data-testid="emp-cct" /></F>
            <F label="Taux d'activité (%)"><Input type="number" step="1" value={form.taux_activite ?? 100} onChange={(e) => set("taux_activite", e.target.value)} data-testid="emp-taux-activite" /></F>
            <F label="Mode de rémunération">
              <Select value={form.mode_remuneration || "mensuel"} onValueChange={(v) => set("mode_remuneration", v)}>
                <SelectTrigger data-testid="emp-mode-remuneration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensuel">Mensuel</SelectItem>
                  <SelectItem value="horaire">Horaire</SelectItem>
                </SelectContent>
              </Select>
            </F>
            {form.mode_remuneration === "horaire" ? (
              <>
                <F label="Salaire horaire (CHF)"><Input type="number" step="0.05" value={form.salaire_horaire ?? ""} onChange={(e) => set("salaire_horaire", e.target.value)} data-testid="emp-salaire-horaire" /></F>
                <F label="Heures de référence / mois"><Input type="number" step="0.5" value={form.heures_mensuelles_ref ?? ""} onChange={(e) => set("heures_mensuelles_ref", e.target.value)} data-testid="emp-heures-ref" /></F>
              </>
            ) : (
              <F label="Salaire brut mensuel (CHF)"><Input type="number" step="0.05" value={form.salaire_brut_mensuel ?? ""} onChange={(e) => set("salaire_brut_mensuel", e.target.value)} data-testid="emp-salaire-mensuel" /></F>
            )}
            <F label="13e salaire">
              <Select value={form.treizieme_mode || "mensualise"} onValueChange={(v) => set("treizieme_mode", v)}>
                <SelectTrigger data-testid="emp-13e-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensualise">Mensualisé</SelectItem>
                  <SelectItem value="annuel">Versement annuel</SelectItem>
                  <SelectItem value="aucun">Aucun</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Taux 13e (%)" hint="8.3333 % = un 13e mois complet">
              <Input type="number" step="0.0001" value={form.treizieme_taux ?? 8.3333} onChange={(e) => set("treizieme_taux", e.target.value)} data-testid="emp-13e-taux" />
            </F>
            {form.treizieme_mode === "annuel" && (
              <F label="Mois de versement du 13e"><Input type="number" min="1" max="12" value={form.treizieme_mois_versement ?? 12} onChange={(e) => set("treizieme_mois_versement", e.target.value)} data-testid="emp-13e-mois" /></F>
            )}
          </TabsContent>

          <TabsContent value="assurances" className="mt-4 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ["assujetti_avs", "AVS / AI / APG"],
                ["assujetti_ac", "Assurance-chômage (AC)"],
                ["assujetti_amat", "Assurance-maternité GE"],
                ["assujetti_aanp", "Accidents non professionnels (AANP)"],
                ["assujetti_ijm", "Indemnité journalière maladie (IJM)"],
                ["cct_assujetti", "Contribution CCT"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={form[k] !== false} onChange={(e) => set(k, e.target.checked)}
                         className="rounded border-gray-300 text-[#52a675]" data-testid={`emp-${k}`} />
                  {label}
                </label>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <F label="Taux AANP spécifique (%)" hint="Laisser vide pour utiliser le taux des paramètres">
                <Input type="number" step="0.001" value={form.taux_aanp_override ?? ""} onChange={(e) => set("taux_aanp_override", e.target.value)} data-testid="emp-aanp-taux" />
              </F>
              <F label="Taux IJM spécifique (%)" hint="Laisser vide pour utiliser le taux des paramètres">
                <Input type="number" step="0.001" value={form.taux_ijm_override ?? ""} onChange={(e) => set("taux_ijm_override", e.target.value)} data-testid="emp-ijm-taux" />
              </F>
              <F label="Contribution CCT">
                <Select value={form.cct_mode || "params"} onValueChange={(v) => set("cct_mode", v)}>
                  <SelectTrigger data-testid="emp-cct-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="params">Selon les paramètres</SelectItem>
                    <SelectItem value="montant">Montant fixe mensuel</SelectItem>
                  </SelectContent>
                </Select>
              </F>
              {form.cct_mode === "montant" && (
                <F label="Montant CCT employé (CHF/mois)">
                  <Input type="number" step="0.05" value={form.cct_montant_mensuel ?? ""} onChange={(e) => set("cct_montant_mensuel", e.target.value)} data-testid="emp-cct-montant" />
                </F>
              )}
            </div>
          </TabsContent>

          <TabsContent value="lpp" className="mt-4 grid sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={form.lpp_assujetti !== false} onChange={(e) => set("lpp_assujetti", e.target.checked)}
                     className="rounded border-gray-300 text-[#52a675]" data-testid="emp-lpp-assujetti" />
              Assujetti à la LPP
            </label>
            <F label="Base de calcul">
              <Select value={form.lpp_mode || "plan"} onValueChange={(v) => set("lpp_mode", v)}>
                <SelectTrigger data-testid="emp-lpp-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="plan">Calcul automatique selon le plan</SelectItem>
                  <SelectItem value="montant_caisse">Montant communiqué par la caisse</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Plan de prévoyance"><Input value={form.lpp_plan_code || "base"} onChange={(e) => set("lpp_plan_code", e.target.value)} data-testid="emp-lpp-plan" /></F>
            <F label="Salaire assuré imposé (CHF/an)" hint="Facultatif : remplace le calcul salaire − déduction de coordination">
              <Input type="number" step="1" value={form.lpp_salaire_assure_manuel ?? ""} onChange={(e) => set("lpp_salaire_assure_manuel", e.target.value)} data-testid="emp-lpp-assure" />
            </F>
            {form.lpp_mode === "montant_caisse" && (
              <>
                <F label="Cotisation mensuelle employé (CHF)">
                  <Input type="number" step="0.05" value={form.lpp_cotisation_mensuelle_employe ?? ""} onChange={(e) => set("lpp_cotisation_mensuelle_employe", e.target.value)} data-testid="emp-lpp-emp" />
                </F>
                <F label="Cotisation mensuelle employeur (CHF)">
                  <Input type="number" step="0.05" value={form.lpp_cotisation_mensuelle_employeur ?? ""} onChange={(e) => set("lpp_cotisation_mensuelle_employeur", e.target.value)} data-testid="emp-lpp-empr" />
                </F>
              </>
            )}
          </TabsContent>

          <TabsContent value="impots" className="mt-4 grid sm:grid-cols-2 gap-3">
            <F label="Régime d'imposition">
              <Select value={form.imposition || "ordinaire"} onValueChange={(v) => set("imposition", v)}>
                <SelectTrigger data-testid="emp-imposition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ordinaire">Ordinaire</SelectItem>
                  <SelectItem value="source">À la source</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Canton fiscal"><Input value={form.canton_fiscal || "GE"} onChange={(e) => set("canton_fiscal", e.target.value)} data-testid="emp-canton" /></F>
            {form.imposition === "source" && (
              <>
                <F label="Barème"><Input value={form.bareme_is || ""} onChange={(e) => set("bareme_is", e.target.value)} placeholder="A0, B1, C2…" data-testid="emp-bareme" /></F>
                <F label="Taux d'impôt à la source (%)" hint="Taux communiqué par l'AFC pour ce barème">
                  <Input type="number" step="0.01" value={form.taux_is ?? ""} onChange={(e) => set("taux_is", e.target.value)} data-testid="emp-taux-is" />
                </F>
                <F label="Situation familiale"><Input value={form.situation_familiale || ""} onChange={(e) => set("situation_familiale", e.target.value)} data-testid="emp-situation" /></F>
              </>
            )}
          </TabsContent>

          {!nouveau && (
            <TabsContent value="evenements" className="mt-4">
              {evts.length === 0 ? (
                <p className="text-sm text-gray-400">Aucun événement salarial enregistré</p>
              ) : (
                <table className="w-full text-sm" data-testid="emp-evenements-table">
                  <thead className="text-left text-[11px] uppercase text-gray-500">
                    <tr><th className="py-1">Type</th><th>Période</th><th className="text-right">Montant</th><th>Statut</th></tr>
                  </thead>
                  <tbody>
                    {evts.map((ev) => (
                      <tr key={ev.id} className="border-t border-gray-100">
                        <td className="py-1.5">{TYPE_LABEL[ev.type] || ev.type}</td>
                        <td className="text-xs text-gray-600">
                          {ev.date_debut || ev.periode}{ev.date_fin ? ` → ${ev.date_fin}` : ""}
                        </td>
                        <td className="text-right font-mono">{ev.montant ? chf(ev.montant) : "—"}</td>
                        <td className="text-xs">{ev.statut === "annule" ? "Annulé" : "Actif"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </TabsContent>
          )}
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="employe-cancel">Annuler</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="employe-save">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {nouveau ? "Créer" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
